'use client';
import React, { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { AlertTriangle, Bell, ChevronRight, LifeBuoy, Plus, ScanLine, ShieldCheck } from 'lucide-react';
import { useCertificates } from '@/contexts/CertificatesContext';
import { useSeafarerProfile } from '@/contexts/SeafarerProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  computeCertificateHealth,
  getCertificateAttentionItems,
  isAlertSuppressed,
  isRequirementReview,
  isAlertableCertificate,
  needsExpiryAttention,
} from '@/lib/certificate-alerts';

const AlertDrawer = dynamic(() => import('@/components/AlertDrawer'), { ssr: false });
import type { Certificate } from '@/lib/db';
import StatusBadge from '@/components/ui/StatusBadge';
import UploadBanner from './UploadBanner';
import { AddCertModal } from './CertificateTable';
import TourTipsButton from '@/components/TourTipsButton';

function getInitials(name: string) {
  return name
    .split(' ')
    .map((p) => p.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning,';
  if (h < 17) return 'Good afternoon,';
  return 'Good evening,';
}

function relativeSync(iso: string | null): string {
  if (!iso) return 'Not synced yet';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'Synced just now';
  if (mins < 60) return `Synced ${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `Synced ${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `Synced ${days}d ago`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Segmented health donut ────────────────────────────────────────────────────
const DONUT_SIZE = 128;
const DONUT_STROKE = 13;
const DONUT_R = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_C = 2 * Math.PI * DONUT_R;

function HealthDonut({ segments, score }: { segments: { value: number; color: string }[]; score: number }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const gap = total > 1 ? 5 : 0; // px gap between segments (in path units)
  let acc = 0;
  return (
    <div className="relative shrink-0" style={{ width: DONUT_SIZE, height: DONUT_SIZE }}>
      <svg width={DONUT_SIZE} height={DONUT_SIZE} className="-rotate-90">
        <circle cx={DONUT_SIZE / 2} cy={DONUT_SIZE / 2} r={DONUT_R} fill="none" stroke="var(--muted)" strokeWidth={DONUT_STROKE} />
        {total > 0 &&
          segments.map((seg, i) => {
            if (seg.value <= 0) return null;
            const frac = seg.value / total;
            const len = Math.max(frac * DONUT_C - gap, 0.5);
            const offset = -acc * DONUT_C;
            acc += frac;
            return (
              <circle
                key={i}
                cx={DONUT_SIZE / 2}
                cy={DONUT_SIZE / 2}
                r={DONUT_R}
                fill="none"
                stroke={seg.color}
                strokeWidth={DONUT_STROKE}
                strokeLinecap="round"
                strokeDasharray={`${len} ${DONUT_C - len}`}
                strokeDashoffset={offset}
              />
            );
          })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[2rem] leading-none font-800 tracking-[-0.03em] text-foreground font-tabular">{score}%</span>
        <span className="text-[11px] font-500 text-muted-foreground mt-0.5">health score</span>
      </div>
    </div>
  );
}

export default function MobileHomeDashboard() {
  const router = useRouter();
  const { certificates, lastSyncedAt } = useCertificates();
  const { data: seafarerData } = useSeafarerProfile();
  const { profile: authProfile } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const urgentCount = useMemo(() => certificates.filter(needsExpiryAttention).length, [certificates]);

  const seafarerProfile = seafarerData?.profile;
  const displayName = authProfile?.full_name || seafarerProfile?.fullName || 'Seafarer';
  const displayRank = authProfile?.rank || seafarerProfile?.currentRank || 'Seafarer';

  const health = useMemo(() => computeCertificateHealth(certificates), [certificates]);
  const attention = useMemo(() => getCertificateAttentionItems(certificates), [certificates]);

  const activeTotal = health.valid + health.expiringSoon + health.expired + health.review;
  const score =
    activeTotal > 0
      ? Math.round(((health.valid * 1 + health.expiringSoon * 0.6 + health.review * 0.5) / activeTotal) * 100)
      : 0;

  const noExpiryCount = useMemo(
    () => certificates.filter((c) => !isAlertSuppressed(c) && !isRequirementReview(c) && c.status === 'no-expiry').length,
    [certificates]
  );

  // Soonest-expiring cert in the "expiring" bucket for the renew banner.
  const nextToRenew = useMemo<Certificate | null>(() => {
    const expiring = certificates.filter(
      (c) => isAlertableCertificate(c) && (c.status === 'critical' || c.status === 'expiring-soon') && c.daysRemaining !== null
    );
    if (expiring.length === 0) return null;
    return [...expiring].sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0))[0];
  }, [certificates]);

  const legend = [
    { key: 'valid', label: 'Valid', count: health.valid, color: 'var(--status-valid)' },
    { key: 'expiring', label: 'Expiring', count: health.expiringSoon, color: 'var(--status-expiring-soon)' },
    { key: 'expired', label: 'Expired', count: health.expired, color: 'var(--status-critical)' },
    { key: 'verify', label: 'Verify', count: health.review, color: 'var(--status-verify)' },
  ];

  return (
    <div className="space-y-4 pb-2">
      {/* Greeting header */}
      <header className="flex items-start justify-between gap-3 pt-1">
        <div className="min-w-0">
          <p className="text-sm font-500 text-muted-foreground">{greeting()}</p>
          <h1 className="text-2xl font-800 tracking-[-0.02em] text-foreground leading-tight truncate">{displayName}</h1>
          <div className="home-status-pill mt-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--status-valid)' }} />
            <span className="truncate">{displayRank} · {relativeSync(lastSyncedAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setAlertOpen(true)}
            className="relative w-11 h-11 rounded-2xl bg-card border border-border flex items-center justify-center text-foreground active:scale-95 transition-transform"
            aria-label={`Certificate alerts${urgentCount > 0 ? ` — ${urgentCount} need attention` : ''}`}
          >
            <Bell size={19} />
            {urgentCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-700 text-white flex items-center justify-center px-1 leading-none"
                style={{ backgroundColor: 'var(--status-critical)' }}
              >
                {urgentCount > 99 ? '99+' : urgentCount}
              </span>
            )}
          </button>
          <Link href="/profile" className="home-avatar" aria-label="Open profile">
            {getInitials(displayName) || 'S'}
          </Link>
        </div>
      </header>
      {alertOpen && <AlertDrawer open={alertOpen} onClose={() => setAlertOpen(false)} />}

      {certificates.length === 0 && (
        <section className="card-elevated p-4 space-y-3">
          <div>
            <h2 className="text-base font-800 text-foreground">Start anywhere</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Explore Seamigo now. Import a profile or add records manually whenever you are ready.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <TourTipsButton />
            <Link
              href="/support"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-600 text-primary hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <LifeBuoy size={14} />
              Help &amp; support
            </Link>
          </div>
        </section>
      )}

      {/* Certificate Health card */}
      <section className="health-card">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[13px] font-700 tracking-wide text-[color:#6B7A89] uppercase">Certificate Health</p>
          <Link href="/certificates" className="text-[13px] font-700 text-primary">View all</Link>
        </div>
        <div className="flex items-center gap-4">
          <HealthDonut
            score={score}
            segments={[
              { value: health.valid, color: 'var(--status-valid)' },
              { value: health.expiringSoon, color: 'var(--status-expiring-soon)' },
              { value: health.expired, color: 'var(--status-critical)' },
              { value: health.review, color: 'var(--status-verify)' },
            ]}
          />
          <div className="flex-1 min-w-0">
            {legend.map((row) => (
              <Link key={row.key} href={`/certificates?status=${row.key}`} className="health-legend-row">
                <span className="health-legend-swatch" style={{ backgroundColor: row.color }} />
                <span className="text-base font-800 font-tabular text-foreground w-5">{row.count}</span>
                <span className="text-sm font-500 text-muted-foreground">{row.label}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
          <span>{health.coveredHistorical} covered · {noExpiryCount} no-expiry</span>
          <span>Imported {formatDate(lastSyncedAt)}</span>
        </div>
      </section>

      {/* Next to renew */}
      {nextToRenew && (
        <button type="button" className="renew-banner" onClick={() => router.push(`/certificates?cert=${encodeURIComponent(nextToRenew.id)}`)}>
          <span className="renew-icon">
            <AlertTriangle size={20} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[11px] font-700 tracking-wide uppercase text-[#b9791a]">Next to renew</span>
            <span className="block text-[15px] font-700 text-[#5a3d12] truncate">{nextToRenew.name}</span>
            <span className="block text-xs text-[#8a6a30]">
              Expires in {nextToRenew.daysRemaining} day{nextToRenew.daysRemaining !== 1 ? 's' : ''} · {nextToRenew.expiryDate}
            </span>
          </span>
          <ChevronRight size={18} className="text-[#b9791a] shrink-0" />
        </button>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <UploadBanner variant="card" />
        <button type="button" className="quick-action-card" onClick={() => setShowAdd(true)}>
          <span className="quick-action-icon" style={{ backgroundColor: 'var(--status-valid-bg)', color: 'var(--status-valid)' }}>
            <Plus size={18} />
          </span>
          <span className="quick-action-title">Add certificate</span>
          <span className="quick-action-sub">Track a new record</span>
        </button>
        <Link href="/vault" className="quick-action-card">
          <span className="quick-action-icon" style={{ backgroundColor: 'var(--status-expiring-soon-bg)', color: 'var(--status-expiring-soon)' }}>
            <ScanLine size={18} />
          </span>
          <span className="quick-action-title">Scan document</span>
          <span className="quick-action-sub">Save to your vault</span>
        </Link>
        <Link href="/certificates" className="quick-action-card">
          <span className="quick-action-icon" style={{ backgroundColor: 'var(--status-verify-bg)', color: 'var(--status-verify)' }}>
            <ShieldCheck size={18} />
          </span>
          <span className="quick-action-title">View certificates</span>
          <span className="quick-action-sub">{certificates.length} tracked</span>
        </Link>
      </div>

      {/* Needs attention */}
      {attention.length > 0 && (
        <section className="pt-2">
          <div className="flex items-center justify-between mb-3 px-0.5">
            <span className="text-[13px] font-700 tracking-wide uppercase" style={{ color: '#6B7A89' }}>Needs attention</span>
            <span className="attention-count-badge">{attention.length}</span>
          </div>
          <div className="bg-card rounded-[22px] overflow-hidden" style={{ boxShadow: '0 1px 2px rgba(13,38,59,.04)' }}>
            {attention.map(({ certificate, severity, reason }, i) => {
              const barColor =
                severity === 'critical'
                  ? 'var(--status-critical)'
                  : severity === 'warning'
                  ? 'var(--status-expiring-soon)'
                  : 'var(--status-verify)';
              const days =
                certificate.daysRemaining === null
                  ? ''
                  : certificate.daysRemaining < 0
                    ? `${Math.abs(certificate.daysRemaining)}d ago`
                    : `${certificate.daysRemaining}d left`;
              return (
                <Link
                  key={certificate.id}
                  href={`/certificates?cert=${encodeURIComponent(certificate.id)}`}
                  className="flex items-center gap-3 px-4 py-[15px] active:bg-muted/50 transition-colors"
                  style={i < attention.length - 1 ? { borderBottom: '1px solid #F2F4F7' } : undefined}
                >
                  <span className="w-2 h-[38px] rounded-[5px] shrink-0" style={{ backgroundColor: barColor }} aria-hidden="true" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14.5px] font-700 text-foreground truncate">{certificate.name}</span>
                    <span className="block text-[12.5px] truncate" style={{ color: '#8A98A6' }}>{certificate.category} · {reason}</span>
                  </span>
                  <span className="text-right shrink-0">
                    <StatusBadge status={certificate.status} label={certificate.actionLabel} />
                    {days && <span className="block text-[11.5px] mt-1" style={{ color: '#9AA6B2' }}>{days}</span>}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {showAdd && <AddCertModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}
